import { create } from 'zustand';
import type { Chamber } from '@/types/raspi';
import { getAllChambers, deleteChamber } from '@/services/api';

interface ChamberStore {
  // Chamber Data (Database-Aligned)
  allChambers: Record<string, Chamber>;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAllChambers: () => Promise<void>;
  updateChamberFromWebSocket: (chamber_id: string, data: Partial<Chamber>) => void;
  updateChamberFromUser: (chamber_id: string, data: Partial<Chamber>) => Promise<boolean>;
  removeChamber: (chamber_id: string) => Promise<boolean>;
  deleteChamber: (chamber_id: string) => Promise<boolean>;
  getChamber: (chamber_id: string) => Chamber | undefined;
  clearError: () => void;
  resetStore: () => void;
}

export const useChamberStore = create<ChamberStore>((set, get) => ({
  // Initialize with default values
  allChambers: {},
  isLoading: false,
  error: null,

  fetchAllChambers: async () => {
    set({ isLoading: true, error: null });
    try {
      const chambersData = await getAllChambers();
      // Convert string dates to Date objects for all chambers
      const convertedChambers: Record<string, Chamber> = {};
      Object.entries(chambersData).forEach(([id, chamber]) => {
        convertedChambers[id] = {
          ...chamber,
          last_update: chamber.last_update instanceof Date ? chamber.last_update : new Date(chamber.last_update),
        };
      });
      // ✅ Fix 2: Merge instead of replace - preserve WebSocket updates
      set((state) => ({
        allChambers: {
          ...state.allChambers,  // Keep existing store data (may have WebSocket updates)
          ...convertedChambers,  // Merge with API data (adds new entities, updates existing)
        },
        isLoading: false,
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch chambers',
        isLoading: false,
        allChambers: {}
      });
    }
  },

  updateChamberFromWebSocket: (chamber_id: string, data: Partial<Chamber>) => {
    set((state) => {
      const prev = state.allChambers[chamber_id];
      const merged: any = { ...prev, ...data };
      // Live pressure/temperature only arrive on measurement broadcasts; the
      // more frequent status broadcasts carry them as null (they're runtime-only
      // on the hub). Preserve the last-known value between measurements while the
      // chamber is still measuring (RUN/READINESS), and clear it once idle so an
      // idle chamber correctly shows "—".
      const measuring = merged.chamber_status === 'RUN' || merged.chamber_status === 'READINESS';
      if (data.chamber_pressure == null) {
        merged.chamber_pressure = measuring ? (prev?.chamber_pressure ?? null) : null;
      }
      if (data.chamber_temperature == null) {
        merged.chamber_temperature = measuring ? (prev?.chamber_temperature ?? null) : null;
      }
      return { allChambers: { ...state.allChambers, [chamber_id]: merged } };
    });
  },

  updateChamberFromUser: async (chamber_id: string, _data: Partial<Chamber>) => {
    
    // VALIDATION: Ensure chamber exists in store before any operation
    const originalData = get().allChambers[chamber_id];
    if (!originalData) {
      console.error('🔄 [CHAMBER STORE] ERROR: Cannot update non-existent chamber:', chamber_id);
      return false;
    }
    
    // VALIDATION: Chamber updates are not supported for now
    console.error('🔄 [CHAMBER STORE] ERROR: Chamber updates not supported');
    return false;
  },

  removeChamber: async (chamber_id: string) => {
    
    // Capture original data BEFORE optimistic update
    const originalData = get().allChambers[chamber_id];
    
    try {
      // Remove from store immediately (store-first approach)
      set((state) => {
        const { [chamber_id]: removed, ...remainingChambers } = state.allChambers;
        return { allChambers: remainingChambers };
      });

      // Sync deletion to database using new specific API method
      const success = await deleteChamber(chamber_id);
      
      if (!success) {
        // Revert store update on failure using captured original data
        set((state) => ({
          allChambers: {
            ...state.allChambers,
            [chamber_id]: originalData,
          },
        }));
        return false;
      }

      return true;
    } catch (error) {
      console.error('🔄 [CHAMBER STORE] Error removing chamber:', error);
      // Also revert on exception using captured original data
      if (originalData) {
        set((state) => ({
          allChambers: {
            ...state.allChambers,
            [chamber_id]: originalData,
          },
        }));
      }
      return false;
    }
  },

  deleteChamber: async (chamber_id: string) => {
    try {
      // Use the new specific API method
      const success = await deleteChamber(chamber_id);
      
      if (success) {
        // Remove from store on successful deletion
        set((state) => {
          const { [chamber_id]: removed, ...remainingChambers } = state.allChambers;
          return { allChambers: remainingChambers };
        });
      }
      
      return success;
    } catch (error) {
      console.error('🔄 [CHAMBER STORE] Error deleting chamber:', error);
      return false;
    }
  },

  getChamber: (chamber_id: string) => {
    return get().allChambers[chamber_id];
  },

  clearError: () => {
    set({ error: null });
  },

  resetStore: () => {
    set({
      allChambers: {},
      isLoading: false,
      error: null,
    });
  },
}));

export default useChamberStore; 